/**
 * Money and magnitude formatting, Indian-numbering.
 *
 * A Blinkit dashboard reads in crore and lakh, not in millions, and the figures
 * on these pages span five orders of magnitude — ₹4,036 of potential sales loss
 * on one row and ₹6.4 Cr of undelivered PO value on another. Printing both in
 * full puts a fourteen-character number in a table cell; printing both in crore
 * rounds the first one to zero. So the unit is chosen per value.
 *
 * Everything here returns an em dash for null. A null is "we have no figure",
 * and the one thing it must never render as is 0.
 */

const DASH = '—';

/** ₹ with the unit that keeps three significant figures readable. */
export function inr(n: number | null | undefined, opts: { sign?: boolean } = {}): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return DASH;
  const sign = n < 0 ? '−' : opts.sign ? '+' : '';
  const v = Math.abs(n);
  if (v >= 1e7) return `${sign}₹${(v / 1e7).toFixed(v / 1e7 >= 100 ? 0 : 2)} Cr`;
  if (v >= 1e5) return `${sign}₹${(v / 1e5).toFixed(v / 1e5 >= 100 ? 0 : 2)} L`;
  if (v >= 1e3) return `${sign}₹${Math.round(v).toLocaleString('en-IN')}`;
  return `${sign}₹${v.toFixed(v < 10 && v % 1 !== 0 ? 2 : 0)}`;
}

/** Full rupees with separators, for tooltips and anywhere precision matters. */
export function inrExact(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return DASH;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

export function units(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return DASH;
  const v = Math.abs(n);
  if (v >= 1e7) return `${(n / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `${(n / 1e5).toFixed(2)} L`;
  return Math.round(n).toLocaleString('en-IN');
}

export function pctOf(n: number | null | undefined, dp = 1): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return DASH;
  return `${n.toFixed(dp)}%`;
}

export function ratioX(n: number | null | undefined, dp = 2): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return DASH;
  return `${n.toFixed(dp)}×`;
}

export function rank(n: number | null | undefined, dp = 1): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return DASH;
  return `#${n.toFixed(dp)}`;
}

export function plain(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return DASH;
  return Math.round(n).toLocaleString('en-IN');
}

/**
 * A delta, signed and parenthesised, matching the Availability tables.
 *
 * `null` returns undefined rather than a dash: the caller renders nothing at
 * all, because an empty delta slot reads cleaner than a dash beside a value
 * that has one.
 */
export function deltaPct(n: number | null | undefined, dp = 1): string | undefined {
  if (n === null || n === undefined || !Number.isFinite(n)) return undefined;
  return `${n >= 0 ? '+' : '−'}${Math.abs(n).toFixed(dp)}%`;
}

/**
 * How far a PERCENTAGE moved, in points.
 *
 * A share that goes from 8.2% to 8.0% has moved 0.2 points, not −2.6 percent.
 * Both are arithmetically true and only the first is what anyone means; the
 * second reads as a collapse when the metric barely moved, and as nothing when
 * a 0.4% share doubles.
 */
export function deltaPoints(n: number | null | undefined, dp = 1): string | undefined {
  if (n === null || n === undefined || !Number.isFinite(n)) return undefined;
  return `${n >= 0 ? '+' : '−'}${Math.abs(n).toFixed(dp)} pts`;
}

export function deltaAbs(n: number | null | undefined, fmt: (v: number) => string): string | undefined {
  if (n === null || n === undefined || !Number.isFinite(n)) return undefined;
  return `${n >= 0 ? '+' : '−'}${fmt(Math.abs(n))}`;
}

/**
 * Which way a delta should read.
 *
 * `invert` is for the metrics where down is good — average rank, ACOS, days to
 * expiry, discount depth. Colouring those green when they rise is the single
 * most common way a dashboard congratulates a team for getting worse.
 */
export function tone(
  n: number | null | undefined,
  invert = false,
): 'up' | 'down' | undefined {
  if (n === null || n === undefined || !Number.isFinite(n) || n === 0) return undefined;
  const good = invert ? n < 0 : n > 0;
  return good ? 'up' : 'down';
}

/** "2026-09-04" → "4 Sep". Month names are fixed, not locale-derived. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return `${d} ${MONTHS[m - 1] ?? ''}`;
}
